import React, {useMemo, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {Header} from '@/components/Header';
import {
	ArrowLeft,
	BadgeCheck,
	ChevronDown,
	Filter,
	Globe2,
	MapPin,
	MessageCircle,
	Phone,
	Search,
	SlidersHorizontal,
	Star,
	X,
} from 'lucide-react';
import {
	categoryMeta,
	entityTypeLabels,
	getCategory,
	mockListings,
	priceRangeLabels,
	ratingLabel,
} from './yellowPagesStaticData';

const sortOptions = [
	{value: 'relevance', label: 'Relevance'},
	{value: 'newest', label: 'Newest'},
	{value: 'rating', label: 'Highest rated'},
];

const ContactIcon = ({type}) => {
	if (type === 'call') return <Phone className="h-3.5 w-3.5"/>;
	if (type === 'website') return <Globe2 className="h-3.5 w-3.5"/>;
	return <MessageCircle className="h-3.5 w-3.5"/>;
};

const SortDropdown = ({value, onChange}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const selected = sortOptions.find(option => option.value === value) || sortOptions[0];

	return (
		<div
			ref={dropdownRef}
			className="relative"
			onBlur={event => {
				if (!dropdownRef.current?.contains(event.relatedTarget)) {
					setIsOpen(false);
				}
			}}
		>
			<button
				type="button"
				onClick={() => setIsOpen(prev => !prev)}
				className="flex min-h-[3rem] w-full items-center justify-between gap-3 rounded-xl border bg-white px-4 text-left shadow-sm transition duration-200"
				style={{
					borderColor: isOpen ? '#D26A53' : '#E5E7EB',
					boxShadow: isOpen ? '0 0 0 3px rgba(210,106,83,0.10)' : 'none',
				}}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
			>
				<span className="flex items-center gap-3">
					<SlidersHorizontal className="h-4 w-4 shrink-0 text-slate-400"/>
					<span className="font-custom-bold text-sm text-slate-800">{selected.label}</span>
				</span>
				<ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}/>
			</button>
			{isOpen && (
				<div
					className="absolute right-0 top-[calc(100%+0.45rem)] z-40 w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl"
					role="listbox"
				>
					{sortOptions.map(option => {
						const active = option.value === value;
						return (
							<button
								key={option.value}
								type="button"
								role="option"
								aria-selected={active}
								onMouseDown={event => event.preventDefault()}
								onClick={() => {
									onChange(option.value);
									setIsOpen(false);
								}}
								className={`flex w-full items-center justify-between px-4 py-3 text-left font-custom-medium text-sm transition hover:bg-[#FDF0ED] ${active ? 'font-custom-semi-bold text-[#D26A53]' : 'text-gray-700'}`}
							>
								{option.label}
								{active && <span className="h-2 w-2 rounded-full bg-[#D26A53]"/>}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

const ResultCard = ({listing}) => {
	const category = getCategory(listing.categories[0]);
	const Icon = category.icon;
	const location = listing.isPhysical ? listing.location : listing.serviceArea;
	const ctaLabel = listing.primaryCta === 'call' ? 'Call' : listing.primaryCta === 'website' ? 'Website' : 'WhatsApp';

	return (
		<article className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg">
			<div className="h-1.5" style={{background: category.color}}/>
			<div className="flex-1 p-4">
				<div className="flex min-w-0 gap-3">
					<div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border"
					     style={{backgroundColor: category.bg, borderColor: `${category.color}30`, color: category.color}}>
						<Icon className="h-7 w-7"/>
					</div>
					<div className="min-w-0 flex-1">
						<div className="mb-1 flex flex-wrap items-center gap-1.5">
							<span className="rounded-md px-2 py-0.5 font-custom-bold text-[0.52rem] uppercase"
							      style={{backgroundColor: `${category.color}14`, color: category.color}}>
								{category.label}
							</span>
							<span className="rounded-md bg-slate-100 px-2 py-0.5 font-custom-bold text-[0.52rem] uppercase text-slate-500">
								{entityTypeLabels[listing.entityType]}
							</span>
							{listing.verification?.isVerified && (
								<span
									className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 font-custom-bold text-[0.52rem] uppercase text-emerald-700">
									<BadgeCheck className="h-2.5 w-2.5"/> Verified
								</span>
							)}
						</div>
						<h2 className="truncate font-custom-extra-bold text-base leading-tight text-slate-950">{listing.entityName}</h2>
						<p className="mt-1 line-clamp-2 font-custom-medium text-[0.74rem] leading-snug text-slate-500">{listing.tagline}</p>
					</div>
				</div>

				<div className="mt-4 grid grid-cols-3 gap-2">
					<div className="rounded-lg bg-slate-50 px-2 py-2">
						<p className="flex items-center gap-1 font-custom-extra-bold text-[0.78rem] text-slate-950">
							<Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400"/>{ratingLabel(listing.rating)}
						</p>
						<p className="font-custom-bold text-[0.52rem] uppercase text-slate-400">Rating</p>
					</div>
					<div className="rounded-lg bg-slate-50 px-2 py-2">
						<p className="truncate font-custom-extra-bold text-[0.78rem] text-slate-950">{priceRangeLabels[listing.priceRange]}</p>
						<p className="font-custom-bold text-[0.52rem] uppercase text-slate-400">Range</p>
					</div>
					<div className="rounded-lg bg-slate-50 px-2 py-2">
						<p className="truncate font-custom-extra-bold text-[0.78rem] text-slate-950">{location}</p>
						<p className="font-custom-bold text-[0.52rem] uppercase text-slate-400">Serves</p>
					</div>
				</div>

				<div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
					<div className="min-w-0">
						<p className="font-custom-bold text-[0.52rem] uppercase tracking-wide text-slate-400">Listed by</p>
						<p className="truncate font-custom-extra-bold text-[0.78rem] text-slate-900">{listing.listedBy.displayName}</p>
						<p className="mt-0.5 flex items-center gap-1 truncate font-custom-medium text-[0.68rem] text-slate-500">
							<MapPin className="h-3 w-3 text-orange-500"/>{listing.listedBy.nativePlace}
						</p>
					</div>
					<button
						className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-orange-600 px-3 py-2 font-custom-bold text-[0.68rem] text-white transition hover:bg-orange-700">
						<ContactIcon type={listing.primaryCta}/> {ctaLabel}
					</button>
				</div>
			</div>
		</article>
	);
};

const FilterBlock = ({title, children}) => (
	<div className="border-b border-slate-100 py-4 last:border-b-0">
		<p className="mb-3 font-custom-extra-bold text-[0.74rem] uppercase tracking-wide text-slate-800">{title}</p>
		{children}
	</div>
);

const CheckboxRow = ({checked, label, onChange}) => (
	<label className="flex cursor-pointer items-center gap-2 py-1.5 font-custom-medium text-sm text-slate-600">
		<input type="checkbox" checked={checked} onChange={onChange}
		       className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"/>
		<span>{label}</span>
	</label>
);

export const YellowPagesSearchStatic = () => {
	const [params] = useSearchParams();
	const [query, setQuery] = useState(params.get('q') || '');
	const [category, setCategory] = useState(params.get('categories') || '');
	const [entityType, setEntityType] = useState('');
	const [priceRange, setPriceRange] = useState('');
	const [isPhysical, setIsPhysical] = useState('');
	const [verifiedOnly, setVerifiedOnly] = useState(false);
	const [sortBy, setSortBy] = useState(params.get('sortBy') || 'relevance');
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

	const results = useMemo(() => {
		const q = query.trim().toLowerCase();
		let items = mockListings.filter(item => {
			if (category && !item.categories.includes(category)) return false;
			if (entityType && item.entityType !== entityType) return false;
			if (priceRange && item.priceRange !== priceRange) return false;
			if (isPhysical && String(item.isPhysical) !== isPhysical) return false;
			if (verifiedOnly && !item.verification?.isVerified) return false;
			if (!q) return true;
			return [
				item.entityName,
				item.tagline,
				item.description,
				item.location,
				item.serviceArea,
				item.listedBy.displayName,
				...item.categories,
			]
				.filter(Boolean)
				.join(' ')
				.toLowerCase()
				.includes(q);
		});

		if (sortBy === 'rating') {
			items = [...items].sort((a, b) => (b.rating.average - a.rating.average) || (b.rating.count - a.rating.count));
		}
		if (sortBy === 'newest') {
			items = [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
		}
		return items;
	}, [query, category, entityType, priceRange, isPhysical, verifiedOnly, sortBy]);

	const clearFilters = () => {
		setCategory('');
		setEntityType('');
		setPriceRange('');
		setIsPhysical('');
		setVerifiedOnly(false);
	};

	const filterPanel = (
		<div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
			<div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
				<div>
					<p className="font-custom-extra-bold text-sm text-slate-950">Filters</p>
					<p className="font-custom-medium text-xs text-slate-500">Refine by category, trust, and service style</p>
				</div>
				<button onClick={clearFilters} className="font-custom-bold text-xs text-orange-600 hover:text-orange-700">Clear</button>
			</div>

			<FilterBlock title="Category">
				<div className="space-y-1">
					{Object.entries(categoryMeta).map(([key, meta]) => (
						<CheckboxRow key={key} label={meta.label} checked={category === key}
						             onChange={() => setCategory(category === key ? '' : key)}/>
					))}
				</div>
			</FilterBlock>

			<FilterBlock title="Entity Type">
				<div className="space-y-1">
					{Object.entries(entityTypeLabels).map(([key, label]) => (
						<CheckboxRow key={key} label={label} checked={entityType === key}
						             onChange={() => setEntityType(entityType === key ? '' : key)}/>
					))}
				</div>
			</FilterBlock>

			<FilterBlock title="Service Mode">
				<div className="space-y-1">
					<CheckboxRow label="Physical location" checked={isPhysical === 'true'}
					             onChange={() => setIsPhysical(isPhysical === 'true' ? '' : 'true')}/>
					<CheckboxRow label="Online / service area" checked={isPhysical === 'false'}
					             onChange={() => setIsPhysical(isPhysical === 'false' ? '' : 'false')}/>
				</div>
			</FilterBlock>

			<FilterBlock title="Price Range">
				<div className="space-y-1">
					{Object.entries(priceRangeLabels).map(([key, label]) => (
						<CheckboxRow key={key} label={label} checked={priceRange === key}
						             onChange={() => setPriceRange(priceRange === key ? '' : key)}/>
					))}
				</div>
			</FilterBlock>

			<FilterBlock title="Trust">
				<CheckboxRow label="Verified listings only" checked={verifiedOnly}
				             onChange={() => setVerifiedOnly(prev => !prev)}/>
			</FilterBlock>
		</div>
	);

	return (
		<>
		<Header/>
		<main className="min-h-screen bg-[#fffaf6] pt-[4.75rem] font-custom-regular text-slate-900 sm:pt-[6.25rem] jk:pt-[5.75rem]">
			<header className="border-b border-orange-100 bg-white/90 backdrop-blur-xl">
				<div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
					<Link to="/yellow-pages-static"
					      className="inline-flex items-center gap-2 font-custom-bold text-xs text-orange-700 hover:text-orange-800">
						<ArrowLeft className="h-3.5 w-3.5"/> Back to Yellow Pages
					</Link>
					<div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
						<div>
							<p className="font-custom-bold text-[0.68rem] uppercase tracking-[0.18em] text-orange-600">Yellow Pages Search</p>
							<h1 className="mt-1 font-custom-black text-2xl leading-tight text-slate-950 sm:text-3xl">Find trusted member businesses and services.</h1>
							<p className="mt-2 max-w-2xl font-custom-medium text-sm leading-relaxed text-slate-500">
								Search by category, service mode, price range, verification, owner context, and location.
							</p>
						</div>
						<button onClick={() => setMobileFiltersOpen(true)}
						        className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-4 py-2.5 font-custom-bold text-sm text-white lg:hidden">
							<Filter className="h-4 w-4"/> Filters
						</button>
					</div>
				</div>
			</header>

			<section className="sticky z-[70] border-b border-orange-100 bg-[#fffaf6]/82 shadow-[0_10px_32px_rgba(124,45,18,0.08)] backdrop-blur-xl portrait:top-[4rem] portrait:sm:top-[5.5rem] portrait:jk:top-[5rem] landscape:top-[3rem] landscape:jk:top-[4rem]">
				<div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
					<div className="grid gap-2 lg:grid-cols-[1fr_13rem]">
						<label className="flex min-h-[3rem] items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 shadow-sm">
							<Search className="h-5 w-5 shrink-0 text-orange-600"/>
							<input value={query} onChange={e => setQuery(e.target.value)}
							       className="w-full bg-transparent font-custom-medium text-sm outline-none placeholder:text-slate-400"
							       placeholder="Search name, tagline, owner, city, category..."/>
						</label>
						<SortDropdown value={sortBy} onChange={setSortBy}/>
					</div>
					<div className="mt-3 flex gap-2 overflow-x-auto pb-1">
						<button onClick={() => setCategory('')}
						        className={`shrink-0 rounded-full px-3 py-2 font-custom-bold text-xs ${!category ? 'bg-slate-950 text-white' : 'bg-white text-slate-600'}`}>
							All categories
						</button>
						{Object.entries(categoryMeta).map(([key, meta]) => {
							const Icon = meta.icon;
							return (
								<button key={key} onClick={() => setCategory(key)}
								        className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-2 font-custom-bold text-xs ${category === key ? 'text-white' : 'bg-white text-slate-600'}`}
								        style={category === key ? {backgroundColor: meta.color} : {}}>
									<Icon className="h-3.5 w-3.5"/>{meta.short}
								</button>
							);
						})}
					</div>
				</div>
			</section>

			<section className="mx-auto grid max-w-7xl gap-5 px-4 py-6 sm:px-6 lg:grid-cols-[17rem_1fr] lg:px-8">
				<aside className="hidden lg:block">{filterPanel}</aside>

				<div>
					<div className="mb-4 flex flex-col gap-3 rounded-2xl border border-orange-100 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="font-custom-extra-bold text-sm text-slate-950">{results.length} matching listings</p>
							<p className="mt-1 font-custom-medium text-xs text-slate-500">
								Use search and filters to narrow the directory to the right member-led listing.
							</p>
						</div>
						<div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-2 font-custom-bold text-xs text-emerald-700">
							<BadgeCheck className="h-3.5 w-3.5"/> Verified listings available
						</div>
					</div>

					{results.length > 0 ? (
						<div className="grid gap-4 md:grid-cols-2">
							{results.map(item => <ResultCard key={item._id} listing={item}/>)}
						</div>
					) : (
						<div className="rounded-[1.25rem] border border-dashed border-orange-200 bg-white p-8 text-center shadow-sm">
							<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-orange-600">
								<Search className="h-6 w-6"/>
							</div>
							<h2 className="mt-4 font-custom-extra-bold text-xl text-slate-950">No matches found</h2>
							<p className="mx-auto mt-2 max-w-md font-custom-medium text-sm leading-relaxed text-slate-500">
								Try clearing filters or searching for food, tax, web, music, interiors, retail, Pune, or Mumbai.
							</p>
							<button onClick={clearFilters}
							        className="mt-5 rounded-full bg-orange-600 px-4 py-2.5 font-custom-bold text-sm text-white hover:bg-orange-700">
								Clear filters
							</button>
						</div>
					)}
				</div>
			</section>

			{mobileFiltersOpen && (
				<div className="fixed inset-0 z-50 bg-slate-950/50 p-3 backdrop-blur-sm lg:hidden"
				     onClick={() => setMobileFiltersOpen(false)}>
					<div className="ml-auto h-full max-w-sm overflow-y-auto rounded-[1.25rem] bg-[#fffaf6] p-3"
					     onClick={e => e.stopPropagation()}>
						<div className="mb-3 flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm">
							<p className="font-custom-extra-bold text-sm text-slate-950">Search filters</p>
							<button onClick={() => setMobileFiltersOpen(false)} className="rounded-full bg-slate-100 p-2 text-slate-600">
								<X className="h-4 w-4"/>
							</button>
						</div>
						{filterPanel}
					</div>
				</div>
			)}
		</main>
		</>
	);
};

export default YellowPagesSearchStatic;
